if (!customElements.get('product-modal')) {
  customElements.define(
    'product-modal',
    class ProductModal extends ModalDialog {
      constructor() {
        super();
        this.currentIndex = 0;

        // IMPORTANT: nothing inside .product-media-modal__content may be cached.
        // On every variant change product-info.js does
        //   modalContent.innerHTML = newModalContent.innerHTML
        // while reusing this element, so any node reference taken in the constructor
        // is detached from then on. Media is therefore always queried live; caching it
        // once left setActive() toggling .active on orphaned nodes and the lightbox
        // rendered blank after switching variants.
        this.addEventListener('keydown', (event) => {
          if (this.mediaItems.length <= 1) return;
          if (event.key === 'ArrowLeft') {
            event.preventDefault();
            this.step(-1);
          } else if (event.key === 'ArrowRight') {
            event.preventDefault();
            this.step(1);
          }
        });

        this.bindControls();
      }

      // Live lookups — see the constructor note.
      get mediaItems() {
        return Array.from(this.querySelectorAll('.product-media-modal__content > [data-media-id]'));
      }

      get currentLabel() {
        return this.querySelector('[data-modal-current]');
      }

      get totalLabel() {
        return this.querySelector('[data-modal-total]');
      }

      // Idempotent: re-runs on every show() so controls keep working even if they are
      // ever swapped out too. The listeners must sit on the buttons themselves, not on
      // this element — ModalDialog registers its own close-on-background pointerup in
      // its constructor, so a delegated listener here would run after it and the modal
      // would already be closing.
      bindControls() {
        const prev = this.querySelector('[data-modal-prev]');
        const next = this.querySelector('[data-modal-next]');
        const counter = this.querySelector('.product-media-modal__counter');

        [prev, next, counter].forEach((el) => {
          if (!el || el.dataset.modalCloseGuard) return;
          el.dataset.modalCloseGuard = 'true';
          el.addEventListener('pointerup', (event) => event.stopPropagation());
        });

        [
          [prev, -1],
          [next, 1],
        ].forEach(([el, delta]) => {
          if (!el || el.dataset.modalNavBound) return;
          el.dataset.modalNavBound = 'true';
          el.addEventListener('click', (event) => {
            event.preventDefault();
            this.step(delta);
          });
        });
      }

      hide() {
        super.hide();
      }

      show(opener) {
        super.show(opener);
        this.bindControls();
        this.showActiveMedia();
      }

      showActiveMedia() {
        const items = this.mediaItems;
        const openedId = this.openedBy ? this.openedBy.getAttribute('data-media-id') : null;
        const idx = items.findIndex((el) => el.getAttribute('data-media-id') === openedId);
        this.setActive(idx >= 0 ? idx : 0);
      }

      step(delta) {
        const total = this.mediaItems.length;
        if (!total) return;
        this.setActive((this.currentIndex + delta + total) % total);
      }

      preloadAround(index) {
        const items = this.mediaItems;
        const total = items.length;
        if (!total) return;

        [index, (index + 1) % total, (index - 1 + total) % total].forEach((i) => {
          const el = items[i];
          if (!el) return;
          const img = el.tagName === 'IMG' ? el : el.querySelector('img');
          if (img && img.loading === 'lazy') img.loading = 'eager';
        });
      }

      setActive(index) {
        const items = this.mediaItems;
        if (!items.length) return;
        this.currentIndex = index;

        items.forEach((el, i) => {
          el.classList.toggle('active', i === index);
        });

        const activeMedia = items[index];

        // Load deferred (e.g. YouTube) content when its slide becomes active.
        if (activeMedia && activeMedia.nodeName === 'DEFERRED-MEDIA') {
          const template = activeMedia.querySelector('template');
          const content = template ? template.content : null;
          if (content && content.querySelector('.js-youtube') && typeof activeMedia.loadContent === 'function') {
            activeMedia.loadContent();
          }
        }

        // Modal images are loading="lazy", so each one only starts downloading the
        // moment it becomes active — every step showed a blank frame first. Warm the
        // current slide and its two neighbours so paging renders instantly.
        this.preloadAround(index);

        const container = this.querySelector('[role="document"]');
        if (container) {
          container.scrollTop = 0;
          container.scrollLeft = 0;
        }

        // Hide arrows / counter when there is only a single image. Recomputed here
        // rather than in the constructor because the media set changes with the variant.
        this.toggleAttribute('data-single', items.length <= 1);

        if (this.currentLabel) this.currentLabel.textContent = index + 1;
        if (this.totalLabel) this.totalLabel.textContent = items.length;
      }
    }
  );
}
